import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, HostBinding, Input, OnDestroy, Output } from '@angular/core';
import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import { merge, Subscription } from 'rxjs';

import { FulfillmentMode } from '../core/models/order.model';
import { CartService } from '../core/services/cart.service';
import { ConfigService } from '../core/services/config.service';
import { SessionService } from '../core/services/session.service';
import { StorefrontService } from '../core/services/storefront.service';
import { UiService } from '../core/services/ui.service';
import { IconComponent } from '../shared/components/icon.component';
import { PriceComponent } from '../shared/components/price.component';
import { TranslatePipe } from '../shared/pipes/translate.pipe';

interface NavLink { id: string; label: string; }

const MODE_ICON: Record<FulfillmentMode, string> = { delivery: 'solar:scooter-bold', pickup: 'solar:bag-smile-bold', dinein: 'solar:chair-2-bold' };
const MODE_LABEL: Record<FulfillmentMode, string> = { delivery: 'Deliver to', pickup: 'Pick up at', dinein: 'Dine in at' };

/**
 * Desktop top bar: brand, primary nav, order-type pill, search, account, cart.
 * Sticky above the scrolling page; the shell decides when it renders.
 */
@Component({
  selector: 'app-desktop-header',
  standalone: true,
  imports: [NgFor, NgIf, AsyncPipe, IconComponent, PriceComponent, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [`
    :host { display: block; flex-shrink: 0; position: relative; z-index: 60; background: var(--fr-surface);
      border-bottom: 1px solid var(--fr-border); box-shadow: 0 1px 3px rgba(16,12,8,0.04);
      transition: background .25s ease, border-color .25s ease, box-shadow .25s ease; }
    :host * { transition: background-color .25s ease, color .25s ease, border-color .25s ease; }
    .in { max-width: 1280px; margin: 0 auto; padding: 0 32px; height: 74px; display: flex; align-items: center; gap: 14px; }
    .brand { background: none; border: none; padding: 0; cursor: pointer; display: flex; align-items: center; gap: 11px; flex-shrink: 0; text-align: start; }
    .mark { width: 42px; height: 42px; border-radius: 11px; overflow: hidden; display: block; box-shadow: var(--fr-shadow-sm); background: var(--fr-surface-3); }
    .mark img { width: 100%; height: 100%; object-fit: contain; display: block; }
    .bn { font-family: var(--brand-display); font-weight: 750; font-size: 17px; letter-spacing: -0.01em; color: var(--fr-text); white-space: nowrap; line-height: 1; }
    .bt { font-size: 11px; font-weight: 600; color: var(--fr-text-3); margin-top: 4px; line-height: 1; }
    nav { display: flex; gap: 4px; margin-inline-start: 6px; }
    nav button { height: 38px; padding: 0 13px; border-radius: 10px; border: none; cursor: pointer; font-family: var(--fr-font);
      font-size: 14.5px; font-weight: 650; background: transparent; color: var(--fr-text-2); white-space: nowrap; }
    nav button.on { font-weight: 750; background: var(--brand-tint); color: var(--brand-deep); }
    .sp { flex: 1; }
    .pill { height: 44px; padding: 0 14px; border-radius: 12px; border: 1px solid var(--fr-border-2); background: var(--fr-surface-2); cursor: pointer;
      display: flex; align-items: center; gap: 10px; font-family: var(--fr-font); max-width: 280px; min-width: 150px; flex: 0 1 auto; }
    .pill .lbl { display: block; white-space: nowrap; font-size: 10.5px; font-weight: 700; color: var(--fr-text-3); text-transform: uppercase; letter-spacing: 0.05em; text-align: start; }
    .pill .val { display: block; font-size: 13.5px; font-weight: 750; color: var(--fr-text); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 190px; text-align: start; }
    .ib { height: 42px; min-width: 42px; padding: 0; border-radius: 11px; border: 1px solid var(--fr-border-2); background: var(--fr-surface); cursor: pointer;
      display: flex; align-items: center; justify-content: center; gap: 8px; flex-shrink: 0; font-family: var(--fr-font); font-size: 14px; font-weight: 700; color: var(--fr-text-2); }
    .ib.txt { padding: 0 14px; }
    .cart { height: 44px; padding: 0 17px; border-radius: 12px; border: none; cursor: pointer; font-family: var(--fr-font); background: var(--brand); color: #fff;
      font-size: 14.5px; font-weight: 750; display: flex; align-items: center; gap: 9px; flex-shrink: 0; box-shadow: 0 4px 12px -5px var(--brand-shadow); }
    .cart .tot { background: rgba(255,255,255,0.22); border-radius: 7px; padding: 2px 7px; font-size: 13px; }
    .burger { display: none; }
    /* compact: nav, account and the order-type pill fold into a dropdown under a hamburger */
    .dd { display: none; max-width: 1280px; margin: 0 auto; padding: 10px 20px 14px; border-top: 1px solid var(--fr-border); flex-direction: column; gap: 2px; }
    .dd.open { display: flex; }
    .dd .l { height: 44px; border-radius: 10px; border: none; background: transparent; color: var(--fr-text-2); font-weight: 700; font-size: 15px; text-align: start;
      padding: 0 14px; cursor: pointer; font-family: var(--fr-font); display: flex; align-items: center; gap: 10px; }
    .dd .l.on { background: var(--brand-tint); color: var(--brand-deep); }
    .dd .pill { width: 100%; max-width: none; margin-top: 8px; }
    .dd .pill .val { max-width: none; }
    /* floating over the home hero at the top of the page: transparent, light text */
    :host(.dark) { background: transparent; border-bottom-color: transparent; box-shadow: none; }
    :host(.dark) .bn { color: #fff; }
    :host(.dark) .bt { color: rgba(255,255,255,0.6); }
    :host(.dark) nav button { color: rgba(255,255,255,0.78); }
    :host(.dark) nav button.on { background: rgba(255,255,255,0.12); color: #fff; }
    :host(.dark) .pill { background: rgba(255,255,255,0.08); border-color: rgba(255,255,255,0.16); }
    :host(.dark) .pill .lbl { color: rgba(255,255,255,0.55); }
    :host(.dark) .pill .val { color: #fff; }
    :host(.dark) .ib { background: transparent; border-color: rgba(255,255,255,0.18); color: #fff; }
    :host(.dark) .dd { border-top-color: rgba(255,255,255,0.12); }
    :host(.dark) .dd .l { color: rgba(255,255,255,0.78); }
    :host(.dark) .dd .l.on { background: rgba(255,255,255,0.12); color: #fff; }
    @media (max-width: 1180px) {
      .in { gap: 10px; padding: 0 20px; height: 64px; }
      .bt, nav, .in > .pill, .in > .acct { display: none; }
      .burger { display: flex; }
    }
  `],
  template: `<div class="in">
      <button class="ib burger" (click)="menuOpen = !menuOpen" [attr.aria-label]="'Menu' | t" [attr.aria-expanded]="menuOpen">
        <app-icon [name]="menuOpen ? 'solar:close-circle-linear' : 'solar:hamburger-menu-linear'" [size]="21"></app-icon>
      </button>
      <button class="brand" (click)="nav.emit(homeOn ? 'home' : 'menu')">
        <span class="mark"><img *ngIf="logo" [src]="logo" alt="" onerror="this.style.display='none'"></span>
        <span>
          <span class="bn" style="display:block">{{ name }}</span>
          <span class="bt" *ngIf="tagline">{{ tagline }}</span>
        </span>
      </button>

      <nav>
        <button *ngFor="let l of links" [class.on]="active === l.id" (click)="go(l.id)">{{ l.label | t }}</button>
      </nav>

      <div class="sp"></div>

      <button class="pill" (click)="ui.openFulfillment()" [title]="'Change' | t">
        <app-icon [name]="modeIcon" [size]="19" [color]="dark ? 'var(--brand-300)' : 'var(--brand-deep)'"></app-icon>
        <span style="min-width:0;flex:1">
          <span class="lbl">{{ modeLabel | t }}</span>
          <span class="val">{{ place | t }}</span>
        </span>
        <app-icon name="solar:alt-arrow-down-linear" [size]="15" [color]="dark ? 'rgba(255,255,255,0.55)' : 'var(--fr-text-3)'"></app-icon>
      </button>

      <button class="ib" (click)="nav.emit('search')" [attr.aria-label]="'Search the menu' | t">
        <app-icon name="solar:magnifer-linear" [size]="20"></app-icon>
      </button>

      <button class="ib txt acct" (click)="account()">
        <app-icon name="solar:user-rounded-linear" [size]="19"></app-icon>
        {{ (session.loggedIn$ | async) ? (firstName || ('Account' | t)) : ('Sign in' | t) }}
      </button>

      <button class="ib txt" (click)="langToggle.emit()" [attr.aria-label]="'Language' | t">
        <app-icon name="solar:global-linear" [size]="17"></app-icon>{{ langLabel }}
      </button>

      <button class="cart" (click)="cart.emit()">
        <app-icon name="solar:bag-4-bold" [size]="18"></app-icon>
        <ng-container *ngIf="(count$ | async) as n; else cartLbl">{{ n }} {{ (n > 1 ? 'items' : 'item') | t }}</ng-container>
        <ng-template #cartLbl>{{ 'Cart' | t }}</ng-template>
        <span *ngIf="(count$ | async)" class="tot fr-num"><app-price [value]="(total$ | async) || 0" [size]="13" [weight]="750" color="#fff"></app-price></span>
      </button>
    </div>

    <!-- collapsed menu (compact widths) -->
    <div class="dd" [class.open]="menuOpen">
      <button *ngFor="let l of links" class="l" [class.on]="active === l.id" (click)="go(l.id)">{{ l.label | t }}</button>
      <button class="l" (click)="menuOpen = false; account()">
        <app-icon name="solar:user-rounded-linear" [size]="19"></app-icon>
        {{ (session.loggedIn$ | async) ? (firstName || ('My account' | t)) : ('Sign in' | t) }}
      </button>
      <button class="pill" (click)="menuOpen = false; ui.openFulfillment()">
        <app-icon [name]="modeIcon" [size]="19" [color]="dark ? 'var(--brand-300)' : 'var(--brand-deep)'"></app-icon>
        <span style="min-width:0;flex:1">
          <span class="lbl">{{ modeLabel | t }}</span>
          <span class="val">{{ place | t }}</span>
        </span>
        <app-icon name="solar:alt-arrow-down-linear" [size]="15" [color]="dark ? 'rgba(255,255,255,0.55)' : 'var(--fr-text-3)'"></app-icon>
      </button>
    </div>`,
})
export class DesktopHeaderComponent implements OnDestroy {
  @Input() active = 'home';
  @Input() lang: 'en' | 'ar' = 'en';
  @Input() homeOn = true;
  /** Floating over the home hero (page at the top) — transparent with light text. */
  @Input() @HostBinding('class.dark') dark = false;
  @Output() nav = new EventEmitter<string>();
  @Output() cart = new EventEmitter<void>();
  @Output() langToggle = new EventEmitter<void>();

  count$ = this.cartSvc.count$;
  total$ = this.cartSvc.total$;
  /** Compact header: hamburger dropdown open state. */
  menuOpen = false;
  private sub: Subscription;

  constructor(
    public session: SessionService,
    public ui: UiService,
    private cartSvc: CartService,
    private config: ConfigService,
    private sf: StorefrontService,
    cdr: ChangeDetectorRef,
  ) {
    // OnPush: the pill / account label read session state that changes outside this view.
    this.sub = merge(session.fulfillment$, session.branch$, session.configured$, session.loggedIn$, session.userName$)
      .subscribe(() => cdr.markForCheck());
  }
  ngOnDestroy(): void { this.sub.unsubscribe(); }

  get links(): NavLink[] {
    const l: NavLink[] = [];
    if (this.homeOn) l.push({ id: 'home', label: 'Home' });
    l.push({ id: 'menu', label: 'Menu' });
    // Reserve opens the create-reservation flow; its list lives under Account.
    if (this.config.reservationsEnabled) l.push({ id: 'reservation', label: 'Reserve' });
    l.push({ id: 'branches', label: 'Branches' });
    return l;
  }
  get logo(): string { return this.sf.brand?.logo || ''; }
  get name(): string { return this.sf.brand?.name || ''; }
  get tagline(): string { return this.sf.brand?.tagline || ''; }
  get langLabel(): string { return this.lang === 'en' ? 'العربية' : 'EN'; }
  get firstName(): string { return (this.session.userName || '').trim().split(/\s+/)[0] || ''; }

  get modeIcon(): string { return MODE_ICON[this.session.fulfillment]; }
  get modeLabel(): string { return MODE_LABEL[this.session.fulfillment]; }
  /** Delivery shows the chosen area; pickup/dine-in the branch. Same source as the phone bar. */
  get place(): string {
    const b = this.session.branch;
    return b.area || b.name || 'Select location';
  }

  /** Navigate from either the inline nav or the collapsed menu. */
  go(id: string): void { this.menuOpen = false; this.nav.emit(id); }

  /** Guest → sign-in dialog; signed in → the Account screen. */
  account(): void {
    this.menuOpen = false;
    if (this.session.loggedIn) this.nav.emit('account');
    else this.ui.requireLogin('Sign in to view your account.');
  }
}
